// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";

import { ICloudParameter, IVirtAssign } from "shared/types/cloudtypes";
import { isSet, roundToDigits } from "shared/utils/utils";
import { ActionType, IAction, IActionTrend, IActionTypeConfirmPopup, raiseAction } from "actions";
import { DataState, TimePeriod } from "types";
import { formatEnergyValue, formatPowerValue, getColor, roundTimestamp } from "guiutils";
import { IState } from "states/states";
import { ITrendDataRow } from "states/trendingstates";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";
import { ITreeViewDropDownOption } from "view/comp/TreeViewDropDown";
import ModalTrendData from "view/modal/ModalTrendData";
import IconEdit from "icons/iconEdit";
import IconCopy from "icons/iconCopy";

import ChartBaseComponent, {
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  mapStateToProps as baseMapStateToProps,
} from "./ChartBase";

// uses webpack definitions
declare const IS_CLOUD: boolean;

const DIGITS_TO_SHOW = 3;

interface ITrendDatatableExtProps extends IChartBaseExtProps {}

interface ITrendDatatableProps extends IChartBaseProps {
  siteId: number | undefined;
  virtualTariffCircuits: IVirtAssign[];
  canModifyData: boolean;
}

const mapStateToProps = (state: IState, ownProps: ITrendDatatableExtProps) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);

  const props: ITrendDatatableProps = {
    ...baseMapStateToProps(state, ownProps),
    siteId: state.cached.selectedSite,
    virtualTariffCircuits: (state.para.parameter as ICloudParameter)?.virtualSite?.tariffline ?? [],
    canModifyData:
      IS_CLOUD &&
      Boolean(curUser?.accessRights.modifySites) &&
      state.gui.trendPeriod.timePeriod === TimePeriod.DAY,
  };
  return props;
};

interface ITrendDatatableDispatch {
  onEdit: (timestamp: number, tlId: number) => void;
  onShowConfirmation: (title: string, message: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendDatatableDispatch = {
    onEdit: (timestamp: number, tlIndex: number) => {
      const payload: IActionTrend = {
        timestamp,
        tlIndex,
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_TRENDDATA, payload));
    },
    onShowConfirmation: (title: string, message: string) => {
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_edit: {
    id: "change value",
  },
  msg_copied_title: {
    id: "Copy Data to Clipboard",
  },
  msg_copied_content: {
    id: "Data was successfully copied to clipboard. You can now paste it into Excel or similar software.",
  },
});

class TrendDatatableComponent extends ChartBaseComponent<
  ITrendDatatableProps & ITrendDatatableExtProps & ITrendDatatableDispatch,
  IChartBaseState
> {
  private selectedTariffCircuits: ITreeViewDropDownOption[] = [];

  private initData() {
    this.selectedTariffCircuits = this.getSelectedTariffCircuits(
      false, // show all tariffs when not in hierarchy mode
      true, // show only selected categories in color
      false, // only color selected tariffs, not the whole hierarchy
    ).tlOptions;
  }

  public render() {
    // must be called in every derrived class from ChartBase!!!
    // the field allTariffCircuits is getting prepared within that function
    this.prepareHierarchyData();

    if (this.props.dataState === DataState.SAVE_REQ) {
      return (
        <div className="loading-spinner-box">
          <div id="loading-spinner"></div>
          <div id="loading-text">
            {this.props.trendLoadingProgress > 0 ?
              <>{Math.round(100 * this.props.trendLoadingProgress)} %</>
            : <></>}
          </div>
        </div>
      );
    }

    if (this.props.dataState === DataState.SAVE_FAIL) {
      return (
        <div className="datatable" style={{ minHeight: 150 }}>
          <FormattedMessage id="Failed to load data." />
        </div>
      );
    }

    this.initData();

    const rows: any[] = [];
    const { startDate, endDate, usedTimestamps } = this.getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.timePeriod,
    );
    const sortedTariffCircuits = this.selectedTariffCircuits.sort(
      (a, b) => a.referenceEntry?.name.localeCompare(b.referenceEntry?.name ?? "") ?? 1,
    );

    // loop through timestamps and sorted tariff circuits
    usedTimestamps.forEach((curTimestamp) => {
      rows.push(this.renderTimestamp(curTimestamp, sortedTariffCircuits));
    });

    return (
      <div className="datatable_container">
        <table className="datatable fullwidthwhite">
          <thead>
            <tr>
              <th>
                <FormattedMessage id="Timestamp" />
              </th>
              {this.selectedTariffCircuits.length > 0 && (
                <th>
                  <FormattedMessage id="Tariffcircuit" />
                </th>
              )}
              {this.props.timePeriod === TimePeriod.DAY && (
                <th className="right optional-xs">
                  <FormattedMessage id="Period" />
                </th>
              )}
              <th className="right">
                <FormattedMessage id="Energy" />
              </th>
              <th className="right">
                <FormattedMessage id="Power" />
              </th>
              <th className="right optional-sm">
                <FormattedMessage id="Target Value" />
              </th>
              <th className="right optional-md">
                <FormattedMessage id="Energy Costs" />
              </th>
              <th className="edit-col">
                <button
                  onClick={() => this.copyToClipboard(sortedTariffCircuits, usedTimestamps)}
                  className="img-link"
                  title={this.context.formatMessage(messages.msg_copied_title)}
                  style={{ border: "none", background: "none", padding: 0 }}
                >
                  <IconCopy />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
          <tfoot>{this.renderSumRow(sortedTariffCircuits)}</tfoot>
        </table>
        <ModalTrendData />
      </div>
    );
  }

  private renderTimestamp(timestamp: number, sortedTariffCircuits: ITreeViewDropDownOption[]) {
    const curRows = this.props.trendData.filter((r) => roundTimestamp(r.timestamp) === timestamp);
    let isFirstRow = true;
    const rowCount = sortedTariffCircuits.filter((curTar) => {
      const tcIndex = this.props.tariffCircuits.findIndex(
        (curItem) => curItem === curTar.referenceEntry,
      );
      return curRows.some((r) => r.tlId === (curTar.referenceEntry?.id ?? tcIndex));
    }).length;
    const tlItems = sortedTariffCircuits.map((curTar) => {
      const tcIndex = this.props.tariffCircuits.findIndex(
        (curItem) => curItem === curTar.referenceEntry,
      );

      const curDataRow = curRows.find((r) => r.tlId === (curTar.referenceEntry?.id ?? tcIndex));
      if (curDataRow) {
        const tariffCircuit = curTar.referenceEntry;
        const color = curTar.color;
        const isVirtualTariffCircuit = this.props.virtualTariffCircuits.some(
          (curVirtTc) =>
            curVirtTc.destIndex === tcIndex && curVirtTc.sourceSite !== this.props.siteId,
        );

        const row = (
          <tr key={`${timestamp}${tcIndex}`}>
            {isFirstRow && (
              <td rowSpan={rowCount}>
                <FormattedDate
                  valueMs={timestamp * 1000}
                  noDay={this.props.timePeriod === TimePeriod.YEAR}
                />
                {[TimePeriod.DAY, TimePeriod.WEEK].includes(this.props.timePeriod) && (
                  <>
                    {" "}
                    <FormattedTime valueMs={timestamp * 1000} />
                  </>
                )}
              </td>
            )}
            {this.selectedTariffCircuits.length > 0 && (
              <td style={{ color }}>{tariffCircuit?.name}</td>
            )}
            {this.props.timePeriod === TimePeriod.DAY && (
              <td className="right nowrap optional-xs">
                {roundToDigits(curDataRow.regperiod, DIGITS_TO_SHOW)} s
              </td>
            )}
            <td className="right nowrap">
              {formatEnergyValue(curDataRow.energy, tariffCircuit?.unit)}
            </td>
            <td className="right nowrap">
              {formatPowerValue(curDataRow.power, tariffCircuit?.unit)}
            </td>
            <td className="right nowrap optional-sm">
              {formatPowerValue(curDataRow.targetpower, tariffCircuit?.unit)}
            </td>
            <td className="right nowrap optional-md">
              {isSet(curDataRow.costs) ?
                `${roundToDigits(curDataRow.costs, DIGITS_TO_SHOW)} EUR`
              : "-"}
            </td>
            {this.props.canModifyData &&
              (isVirtualTariffCircuit ?
                <td></td>
              : <td
                  onClick={() => {
                    this.props.onEdit(curDataRow.timestamp, tcIndex);
                  }}
                  className="edit-col img-link"
                  title={this.context.formatMessage(messages.msg_edit)}
                >
                  <IconEdit />
                </td>)}
          </tr>
        );
        isFirstRow = false;
        return row;
      } else {
        // this may happen on inactive tariff lines
        //console.error("data row not found", curTimeData, tlId);
        return <React.Fragment key={`${timestamp}${tcIndex}`}></React.Fragment>;
      }
    });
    return <React.Fragment key={timestamp}>{tlItems}</React.Fragment>;
  }

  private renderSumRow(sortedTariffs: ITreeViewDropDownOption[]) {
    // loop through all trend data to find all tariff circuit ids
    const tlIds = [...new Set(this.props.trendData.map((r) => r.tlId))];
    let isFirstRow = true;
    const tlItems = sortedTariffs.map((curTar) => {
      const curTlId = curTar.entryId.at(-1);
      if (!isSet(curTlId) || !tlIds.includes(curTlId)) {
        return <React.Fragment key={curTar.entryId.join("-")}></React.Fragment>;
      }
      const isRegulation = this.props.trendData.some(
        (r) => r.tlId === curTlId && isSet(r.targetpower),
      );
      const hasCosts = this.props.trendData.some((r) => r.tlId === curTlId && isSet(r.costs));
      const tariffCircuit = this.props.tariffCircuits.find(
        (curItem, curIndex) => (curItem.id ?? curIndex) === curTlId,
      );
      const color = sortedTariffs.length > 1 ? getColor(curTlId) : "black";
      const row = (
        <tr key={`sum_${curTlId}`}>
          {isFirstRow && (
            <th rowSpan={tlIds.length}>
              <FormattedMessage id="Sum / Max" />
            </th>
          )}
          {this.selectedTariffCircuits.length > 0 && (
            <td style={{ color }}>{tariffCircuit?.name}</td>
          )}
          {this.props.timePeriod === TimePeriod.DAY && (
            <td className="right nowrap optional-xs">
              {`${roundToDigits(this.getTrendSum(curTlId, "regperiod"), DIGITS_TO_SHOW)} s`}
            </td>
          )}
          <td className="right nowrap">
            {formatEnergyValue(this.getTrendSum(curTlId, "energy"), tariffCircuit?.unit)}
          </td>
          <td className="right nowrap">
            {formatPowerValue(this.getTrendMax(curTlId, "power"), tariffCircuit?.unit)}
          </td>
          <td className="right nowrap optional-sm">
            {isRegulation ?
              formatPowerValue(this.getTrendMax(curTlId, "targetpower"), tariffCircuit?.unit)
            : "-"}
          </td>
          <td className="right nowrap optional-md">
            {hasCosts ?
              `${roundToDigits(this.getTrendSum(curTlId, "costs"), DIGITS_TO_SHOW)} EUR`
            : "-"}
          </td>
          {this.props.canModifyData && <td></td>}
        </tr>
      );
      isFirstRow = false;
      return row;
    });
    return <React.Fragment>{tlItems}</React.Fragment>;
  }

  private getTrendSum(tlId: number, trendMember: keyof ITrendDataRow) {
    return this.props.trendData
      .filter((r) => r.tlId === tlId)
      .reduce((prev, r) => prev + ((r[trendMember] as number) ?? 0), 0);
  }
  private getTrendMax(tlId: number, trendMember: keyof ITrendDataRow) {
    return this.props.trendData
      .filter((r) => r.tlId === tlId && isSet(r[trendMember]))
      .reduce((prev, r) => Math.max(prev, r[trendMember] as number), 0);
  }

  private copyToClipboard(
    sortedTariffCircuits: ITreeViewDropDownOption[],
    usedTimestamps: number[],
  ) {
    const rows: string[] = [];

    // build header
    const header: string[] = ["Timestamp"];
    if (this.selectedTariffCircuits.length > 0) {
      header.push("Tariffcircuit");
    }
    if (this.props.timePeriod === TimePeriod.DAY) {
      header.push("Period");
    }
    header.push("Energy", "Power", "Target Value", "Energy Costs");
    rows.push(header.join("\t"));

    // build data rows
    usedTimestamps.forEach((curTimestamp) => {
      const curRows = this.props.trendData.filter(
        (r) => roundTimestamp(r.timestamp) === curTimestamp,
      );
      const filledTariffs = sortedTariffCircuits.filter((curTar) => {
        const tcIndex = this.props.tariffCircuits.findIndex(
          (curItem) => curItem === curTar.referenceEntry,
        );
        return curRows.some((r) => r.tlId === (curTar.referenceEntry?.id ?? tcIndex));
      });

      // format timestamp once for all rows of this timestamp
      const dateStr = new Date(curTimestamp * 1000).toLocaleDateString();
      let timeStr = "";
      if ([TimePeriod.DAY, TimePeriod.WEEK].includes(this.props.timePeriod)) {
        timeStr = new Date(curTimestamp * 1000).toLocaleTimeString();
      }
      const timestampStr = timeStr ? `${dateStr} ${timeStr}` : dateStr;

      // build a row for each tariff circuit
      filledTariffs.forEach((curTar) => {
        const tcIndex = this.props.tariffCircuits.findIndex(
          (curItem) => curItem === curTar.referenceEntry,
        );
        const curDataRow = curRows.find((r) => r.tlId === (curTar.referenceEntry?.id ?? tcIndex));

        if (curDataRow) {
          const tariffCircuit = curTar.referenceEntry;
          const row: string[] = [timestampStr];

          if (this.selectedTariffCircuits.length > 0) {
            row.push(tariffCircuit?.name ?? "");
          }

          if (this.props.timePeriod === TimePeriod.DAY) {
            row.push(String(roundToDigits(curDataRow.regperiod ?? 0, DIGITS_TO_SHOW)));
          }

          row.push(
            String(roundToDigits(curDataRow.energy, DIGITS_TO_SHOW)),
            String(roundToDigits(curDataRow.power, DIGITS_TO_SHOW)),
            String(roundToDigits(curDataRow.targetpower ?? 0, DIGITS_TO_SHOW)),
            isSet(curDataRow.costs) ?
              String(roundToDigits(curDataRow.costs ?? 0, DIGITS_TO_SHOW))
            : "-",
          );

          rows.push(row.join("\t"));
        }
      });
    });

    const csvContent = rows.join("\n");
    navigator.clipboard.writeText(csvContent).then(() => {
      this.props.onShowConfirmation(
        this.context.formatMessage(messages.msg_copied_title),
        this.context.formatMessage(messages.msg_copied_content),
      );
    });
  }
}

const TrendDatatable = connect(mapStateToProps, mapDispatchToProps)(TrendDatatableComponent);

export default TrendDatatable;
